/*
  Web3 helper — Polygon Mainnet only.
  Uses ethers v6 + browser window.ethereum (EIP-1193).
*/
import { BrowserProvider, Contract, formatUnits, parseUnits } from "ethers";

export const POLYGON_CHAIN_ID_DEC = Number(process.env.REACT_APP_POLYGON_CHAIN_ID || 137);
export const POLYGON_CHAIN_ID_HEX = "0x" + POLYGON_CHAIN_ID_DEC.toString(16);
export const POLYGON_RPC = process.env.REACT_APP_POLYGON_RPC_URL || "https://polygon-rpc.com";
export const USDT_ADDR = (process.env.REACT_APP_POLYGON_USDT_CONTRACT || "0xc2132D05D31c914a87C6611C10748AEb04B58e8F").toLowerCase();
export const SPLITTER_ADDR = (process.env.REACT_APP_TGC_SPLITTER_CONTRACT || "").trim();

// ERC-20 minimal ABI
const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function approve(address spender, uint256 amount) returns (bool)",
];

// TGC Splitter ABI — the on-chain contract that receives USDT and distributes 90/10
const SPLITTER_ABI = [
  "function registerInTeam(address referrer) payable",
  "function registerInTeamUSDT(address referrer, uint256 amount)",
];

export function hasInjectedWallet() {
  return typeof window !== "undefined" && !!window.ethereum;
}

export async function connectInjectedWallet() {
  if (!hasInjectedWallet()) throw new Error("Web3 wallet не обнаружен. Установите MetaMask или TrustWallet.");
  const provider = new BrowserProvider(window.ethereum, "any");
  const accounts = await provider.send("eth_requestAccounts", []);
  if (!accounts?.length) throw new Error("Аккаунт не выбран в кошельке");
  return { provider, address: accounts[0].toLowerCase() };
}

/** Auto-switch (or add) Polygon Mainnet in the connected wallet. */
export async function ensurePolygonMainnet() {
  if (!hasInjectedWallet()) throw new Error("Wallet не подключен");
  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: POLYGON_CHAIN_ID_HEX }],
    });
    return true;
  } catch (err) {
    if (err && (err.code === 4902 || String(err.message || "").includes("Unrecognized chain"))) {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: POLYGON_CHAIN_ID_HEX,
          chainName: "Polygon Mainnet",
          nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
          rpcUrls: [POLYGON_RPC],
          blockExplorerUrls: ["https://polygonscan.com/"],
        }],
      });
      return true;
    }
    throw err;
  }
}

export async function getUSDTBalance(provider, address) {
  const usdt = new Contract(USDT_ADDR, ERC20_ABI, provider);
  const [bal, dec] = await Promise.all([usdt.balanceOf(address), usdt.decimals()]);
  return { raw: bal, decimals: Number(dec), formatted: Number(formatUnits(bal, dec)) };
}

/** Execute the tier-based USDT payment (€10 / €50 / €250).
 *  If TGC_SPLITTER_CONTRACT is set → call registerInTeamUSDT(referrer, amount) on it.
 *  Otherwise throws (dry-run path — no contract deployed).
 */
export async function executeTenUsdtSplitPayment({ provider, referrerAddress, amountUsdt = 10 }) {
  if (!provider) throw new Error("Wallet не подключен");
  const signer = await provider.getSigner();
  const usdt = new Contract(USDT_ADDR, ERC20_ABI, signer);
  const decimals = Number(await usdt.decimals());
  const amount = parseUnits(String(amountUsdt), decimals);

  if (!SPLITTER_ADDR) {
    throw new Error(`TGC Splitter contract пока не задеплоен. Оплата ${amountUsdt} USDT будет активирована после публикации контракта в Polygon Mainnet.`);
  }

  const splitter = new Contract(SPLITTER_ADDR, SPLITTER_ABI, signer);

  // ERC-20 approve (allow splitter to pull the tier amount)
  const approveTx = await usdt.approve(SPLITTER_ADDR, amount);
  await approveTx.wait();

  const ref = referrerAddress || "0x0000000000000000000000000000000000000000";
  const tx = await splitter.registerInTeamUSDT(ref, amount);
  return tx; // caller can `await tx.wait()`
}

export function shortAddr(a) {
  if (!a) return "";
  return a.slice(0, 6) + "…" + a.slice(-4);
}
